import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { URGENCY, formatDate } from "@/lib/dates";
import { fmtMoney } from "@/lib/time";
import { useT, useDaysLabel } from "@/i18n";
import { Briefcase, CalendarClock, AlertTriangle, ListTodo, BellRing, Euro, Receipt } from "lucide-react";

const GROUPS = [
  { key: "overdue", testid: "deadline-group-overdue" },
  { key: "due_3", testid: "deadline-group-due-3" },
  { key: "due_7", testid: "deadline-group-due-7" },
  { key: "due_14", testid: "deadline-group-due-14" },
  { key: "later", testid: "deadline-group-later" },
];

function DeadlineRow({ d }) {
  const u = URGENCY[urgencyKey(d)];
  const daysLbl = useDaysLabel();
  return (
    <Link
      to={`/matters/${d.matter_id}`}
      data-testid={`dashboard-deadline-${d.id}`}
      className={`block bg-white border border-slate-200 border-l-4 ${u.bar} rounded-md px-5 py-3.5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-150`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-800">{d.description}</div>
          <div className="text-xs text-slate-500 mt-0.5 truncate">
            {d.matter_name}
            {d.client ? ` · ${d.client}` : ""}
            {d.deadline_type ? ` · ${d.deadline_type}` : ""}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-semibold text-slate-800">{formatDate(d.due_date)}</div>
          <div className={`text-xs mt-0.5 ${d.days < 0 ? "text-red-700 font-semibold" : "text-slate-500"}`}>
            {daysLbl(d.due_date)}
          </div>
        </div>
      </div>
    </Link>
  );
}

function urgencyKey(d) {
  if (d.days < 0) return "overdue";
  if (d.days <= 3) return "due_3";
  if (d.days <= 7) return "due_7";
  if (d.days <= 14) return "due_14";
  return "later";
}

const TASK_PRIORITY_BADGE = {
  High: "bg-red-50 text-red-800 border-red-200",
  Medium: "bg-amber-50 text-amber-800 border-amber-200",
  Low: "bg-slate-100 text-slate-600 border-slate-200",
};

function TaskRow({ t: task }) {
  const { t } = useT();
  const label =
    task.days === null || task.days === undefined
      ? t("days.no_due_date")
      : task.days < 0
        ? Math.abs(task.days) === 1
          ? t("days.overdue_by_one")
          : t("days.overdue_by", { n: Math.abs(task.days) })
        : task.days === 0
          ? t("days.due_today").toLowerCase()
          : task.days === 1
            ? t("days.due_in_one")
            : t("days.due_in", { n: task.days });
  const overdue = task.days !== null && task.days < 0;
  const soon = !overdue && task.days !== null && task.days <= 3;
  const bar = overdue ? "border-l-red-600" : soon ? "border-l-amber-600" : "border-l-slate-300";
  return (
    <Link
      to={`/matters/${task.matter_id}`}
      data-testid={`dashboard-task-${task.id}`}
      className={`block bg-white border border-slate-200 border-l-4 ${bar} rounded-md px-5 py-3.5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-150`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-800">{task.description}</div>
          <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs">
            <span className="text-slate-500 truncate">
              {task.matter_name}
              {task.client ? ` · ${task.client}` : ""}
            </span>
            <span className={`px-2 py-0.5 rounded-full border ${TASK_PRIORITY_BADGE[task.priority] || ""}`}>
              {t(`val.${task.priority}`)}
            </span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-semibold text-slate-800">{formatDate(task.due_date)}</div>
          <div
            className={`text-xs mt-0.5 ${overdue ? "text-red-700 font-semibold" : soon ? "text-amber-700 font-semibold" : "text-slate-500"}`}
            data-testid={`dashboard-task-duelabel-${task.id}`}
          >
            {label}
          </div>
        </div>
      </div>
    </Link>
  );
}

function StatCard({ testid, icon: Icon, label, value, accent }) {
  return (
    <div
      data-testid={testid}
      className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <div className="mt-3 font-serif text-4xl font-semibold text-slate-900" data-testid={`${testid}-value`}>
        {value}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { t } = useT();
  const daysLbl = useDaysLabel();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="text-sm text-slate-500" data-testid="dashboard-loading">
        {t("dashboard.loading")}
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-slate-900">{t("dashboard.title")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("dashboard.subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dashboard-stats">
        <StatCard testid="stat-active-matters" icon={Briefcase} label={t("dashboard.stat_active_matters")} value={data.active_matters} accent="text-slate-700" />
        <StatCard testid="stat-upcoming-deadlines" icon={CalendarClock} label={t("dashboard.stat_upcoming")} value={data.upcoming_deadlines} accent="text-sky-700" />
        <StatCard testid="stat-overdue-deadlines" icon={AlertTriangle} label={t("dashboard.stat_overdue")} value={data.overdue_deadlines} accent="text-red-700" />
        <StatCard testid="stat-open-tasks" icon={ListTodo} label={t("dashboard.stat_open_tasks")} value={data.open_tasks} accent="text-amber-700" />
        <StatCard testid="stat-unbilled-time" icon={Euro} label={t("dashboard.stat_unbilled_time")} value={fmtMoney(data.unbilled_time_total)} accent="text-emerald-700" />
        <StatCard testid="stat-unbilled-expenses" icon={Receipt} label={t("dashboard.stat_unbilled_expenses")} value={fmtMoney(data.unbilled_expenses_total)} accent="text-emerald-700" />
        <Link to="/receivables?outstanding=1" data-testid="stat-outstanding-link">
          <StatCard testid="stat-outstanding" icon={Euro} label={t("dashboard.stat_outstanding")} value={fmtMoney(data.outstanding_total)} accent="text-amber-700" />
        </Link>
        <Link to="/receivables?status=overdue" data-testid="stat-overdue-invoices-link">
          <StatCard testid="stat-overdue-invoices" icon={AlertTriangle} label={`${t("dashboard.stat_overdue_invoices")} (${data.overdue_invoice_count})`} value={fmtMoney(data.overdue_total)} accent="text-red-700" />
        </Link>
      </div>

      {data.reminders_due.length > 0 && (
        <div
          data-testid="reminders-due-banner"
          className="bg-amber-50 border border-amber-200 rounded-lg p-5"
        >
          <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
            <BellRing className="h-4 w-4" /> {t("dashboard.reminders_reached", { n: data.reminders_due.length })}
          </div>
          <ul className="mt-3 space-y-1.5">
            {data.reminders_due.map((d) => (
              <li key={d.id} data-testid={`reminder-due-${d.id}`} className="text-sm text-amber-900">
                <Link to={`/matters/${d.matter_id}`} className="hover:underline">
                  {d.description}
                </Link>
                <span className="text-amber-700"> — {d.matter_name}, {daysLbl(d.due_date)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-8" data-testid="deadline-timeline">
        {GROUPS.map(({ key, testid }) => {
          const items = data.groups[key];
          if (items.length === 0) return null;
          const u = URGENCY[key];
          return (
            <section key={key} data-testid={testid}>
              <div className="flex items-center gap-2.5 mb-3">
                <span className={`h-2.5 w-2.5 rounded-full ${u.dot}`} />
                <h2 className="font-serif text-xl text-slate-800">{t(`dashboard.group_${key}`)}</h2>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${u.badge}`}>{items.length}</span>
              </div>
              <div className="space-y-2.5">
                {items.map((d) => (
                  <DeadlineRow key={d.id} d={d} />
                ))}
              </div>
            </section>
          );
        })}
        {Object.values(data.groups).every((g) => g.length === 0) && (
          <div className="text-sm text-slate-500" data-testid="no-open-deadlines">
            {t("dashboard.no_open_deadlines")}
          </div>
        )}
      </div>

      <section data-testid="open-tasks-section">
        <div className="flex items-center gap-2.5 mb-3">
          <ListTodo className="h-4 w-4 text-slate-600" />
          <h2 className="font-serif text-xl text-slate-800">{t("dashboard.open_tasks")}</h2>
          <span className="text-xs px-2 py-0.5 rounded-full border bg-slate-50 text-slate-600 border-slate-200">
            {data.open_tasks_list.length}
          </span>
        </div>
        {data.open_tasks_list.length === 0 ? (
          <div className="text-sm text-slate-500" data-testid="no-open-tasks">{t("dashboard.no_open_tasks")}</div>
        ) : (
          <div className="space-y-2.5">
            {data.open_tasks_list.map((t) => (
              <TaskRow key={t.id} t={t} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
